import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Calendar, LinkIcon, LogOut, ExternalLink, Activity } from "lucide-react";
import Logo from "../../components/Logo";
import { useAuth } from "../../lib/auth";

const NAV = [
  { to: "/admin/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/organizers", label: "Organizer Links", icon: LinkIcon },
  { to: "/admin/system", label: "System Monitor", icon: Activity },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="admin-layout">
      <aside className="hidden md:flex w-64 shrink-0 bg-white border-r border-[#E2E8F0] flex-col">
        <div className="px-6 h-16 flex items-center border-b border-[#E2E8F0]">
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#5B4CFF] text-white"
                    : "text-[#0F172A] hover:bg-[#F8FAFC]"
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[#E2E8F0] p-4">
          <p className="text-xs text-[#475569] mb-2 font-mono tracking-wider truncate">{user?.email}</p>
          <p className="kicker mb-3">SUPER ADMIN</p>
          <div className="flex gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold border border-[#E2E8F0] hover:border-[#5B4CFF] px-3 py-2 transition-colors"
              data-testid="admin-view-site-btn"
            >
              <ExternalLink size={12} /> View site
            </a>
            <button
              onClick={handleLogout}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-[#0F172A] hover:bg-black text-white px-3 py-2 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#E2E8F0]">
        <div className="px-4 h-14 flex items-center justify-between">
          <Logo />
          <button onClick={handleLogout} className="text-xs font-semibold" data-testid="admin-mobile-logout">Sign out</button>
        </div>
        <div className="flex gap-1 px-2 pb-2 overflow-x-auto">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium whitespace-nowrap border ${
                  isActive ? "bg-[#5B4CFF] text-white border-[#5B4CFF]" : "border-[#E2E8F0] text-[#0F172A]"
                }`
              }
            >
              <Icon size={12} /> {label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 md:ml-0 mt-24 md:mt-0">
        <Outlet />
      </main>
    </div>
  );
}
